/**
 * Archie Platform Environment Definitions — Single Source of Truth
 *
 * Deployment topology:
 *   Helsinki (local dev) → Preview (Render, develop) → Alpha (Render, alpha)
 *   → Staging (Azure East US 2, staging) → Production (Azure, main)
 *
 * Production has 3 geo-regions: US (East US 2), UK (UK West), AU (Australia East).
 * Demo is a standalone branch deployed to Render.
 */

export type ArchieEnvironment =
  | 'helsinki'   // Local dev (archie-platform-v2 on this machine)
  | 'preview'    // Render.com — auto-deploys from develop branch
  | 'alpha'      // Render.com — alpha integration branch
  | 'demo'       // Render.com — demo branch
  | 'staging'    // Azure East US 2 — staging branch, Pulumi-managed
  | 'prod-us'    // Azure East US 2 — main branch (US production)
  | 'prod-uk'    // Azure UK West — main branch (UK production)
  | 'prod-au';   // Azure Australia East — main branch (AU production)

export interface EnvironmentConfig {
  id: ArchieEnvironment;
  label: string;
  shortLabel: string;   // For badges/pills (e.g. "US", "UK")
  api: string;
  frontend?: string;
  branch: string;
  hosting: 'local' | 'render' | 'azure';
  region?: string;       // Azure region name
  production: boolean;   // True for prod-* environments
}

export const ENVIRONMENTS: EnvironmentConfig[] = [
  {
    id: 'helsinki', label: 'Helsinki (Local)', shortLabel: 'LOCAL',
    api: 'http://localhost:8001', branch: '—', hosting: 'local', production: false,
  },
  {
    id: 'preview', label: 'Preview', shortLabel: 'PREVIEW',
    api: 'https://api-preview.heyarchie.ai', frontend: 'https://preview.heyarchie.ai',
    branch: 'develop', hosting: 'render', production: false,
  },
  {
    id: 'alpha', label: 'Alpha', shortLabel: 'ALPHA',
    api: 'https://api-alpha.heyarchie.ai',
    branch: 'alpha', hosting: 'render', production: false,
  },
  {
    id: 'demo', label: 'Demo', shortLabel: 'DEMO',
    api: 'https://api-demo.heyarchie.com',
    branch: 'demo', hosting: 'render', production: false,
  },
  {
    id: 'staging', label: 'Staging', shortLabel: 'STAGING',
    api: 'https://api-staging.heyarchie.com', frontend: 'https://staging.heyarchie.com',
    branch: 'staging', hosting: 'azure', region: 'East US 2', production: false,
  },
  {
    id: 'prod-us', label: 'Production US', shortLabel: 'US',
    api: 'https://api-us.heyarchie.com', frontend: 'https://app.heyarchie.com',
    branch: 'main', hosting: 'azure', region: 'East US 2', production: true,
  },
  {
    id: 'prod-uk', label: 'Production UK', shortLabel: 'UK',
    api: 'https://api-uk.heyarchie.com', frontend: 'https://uk.heyarchie.ai',
    branch: 'main', hosting: 'azure', region: 'UK West', production: true,
  },
  {
    id: 'prod-au', label: 'Production AU', shortLabel: 'AU',
    api: 'https://api-au.heyarchie.com', frontend: 'https://au.heyarchie.com',
    branch: 'main', hosting: 'azure', region: 'Australia East', production: true,
  },
];

/** All production environments */
export const PRODUCTION_ENVIRONMENTS = ENVIRONMENTS.filter(e => e.production);

/** Non-production environments (for dev/staging views) */
export const NON_PRODUCTION_ENVIRONMENTS = ENVIRONMENTS.filter(e => !e.production);

/** Environment groups for deployment cards (preview, staging, 3 x production) */
export const DEPLOYMENT_ENVIRONMENT_IDS: ArchieEnvironment[] = [
  'preview', 'staging', 'prod-us', 'prod-uk', 'prod-au',
];

/** Color scheme for environment badges — each environment has a distinct visual identity */
export const ENV_COLORS: Record<string, { border: string; bg: string; badge: string }> = {
  // Local dev — neutral/slate, monospace dev aesthetic
  'helsinki':  { border: 'border-neutral-300',  bg: 'bg-neutral-100/50',  badge: 'bg-neutral-200 text-neutral-700 border border-neutral-300' },
  // CI/CD envs — informational blues & purples
  'preview':   { border: 'border-blue-200',      bg: 'bg-blue-50/40',      badge: 'bg-blue-100 text-blue-700 border border-blue-200' },
  'alpha':     { border: 'border-violet-200',    bg: 'bg-violet-50/40',    badge: 'bg-violet-100 text-violet-700 border border-violet-200' },
  'demo':      { border: 'border-cyan-200',      bg: 'bg-cyan-50/40',      badge: 'bg-cyan-100 text-cyan-700 border border-cyan-200' },
  // Pre-production — amber caution
  'staging':   { border: 'border-amber-300',     bg: 'bg-amber-50/50',     badge: 'bg-amber-100 text-amber-800 border border-amber-300' },
  // Production regions — each gets a distinct identity
  'prod-us':   { border: 'border-emerald-300',   bg: 'bg-emerald-50/40',   badge: 'bg-emerald-100 text-emerald-800 border border-emerald-300' },
  'prod-uk':   { border: 'border-teal-300',      bg: 'bg-teal-50/40',      badge: 'bg-teal-100 text-teal-800 border border-teal-300' },
  'prod-au':   { border: 'border-orange-300',    bg: 'bg-orange-50/40',    badge: 'bg-orange-100 text-orange-800 border border-orange-300' },
};

/** Get config for a given environment ID */
export function getEnvConfig(id: ArchieEnvironment): EnvironmentConfig | undefined {
  return ENVIRONMENTS.find(e => e.id === id);
}
